import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { fadeUp } from '../utils/animations';
import SafeIcon from '../common/SafeIcon';

const { FiChevronLeft, FiChevronRight, FiStar } = FiIcons;

const TESTIMONIALS = [
  {
    name: "Marcus T.",
    role: "CEO, Elevate Digital Group",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop&crop=face",
    quote: "Dr. Jeff didn't just consult — he transformed how our entire leadership team thinks about AI. He made it human, accessible, and immediately actionable. We left with a roadmap and the confidence to execute it.",
    stars: 5,
    tag: "Business Consulting",
  },
  {
    name: "Priya M.",
    role: "Director of Learning & Development",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&crop=face",
    quote: "The AI Mavericks program changed everything for our team. We went from intimidated to innovative in just a few sessions. Dr. Jeff's ability to simplify complex concepts is truly a gift.",
    stars: 5,
    tag: "Coaching & Training",
  },
  {
    name: "David C.",
    role: "Founder, Clarity Works Co.",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120&h=120&fit=crop&crop=face",
    quote: "Having Dr. Jeff on our podcast was one of our highest-rated episodes ever. He brings depth, warmth, and credibility that resonates with any audience. Absolutely recommend him for any media opportunity.",
    stars: 5,
    tag: "Media & Podcasts",
  },
  {
    name: "Latasha W.",
    role: "Executive Director, Jet Foundation",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&h=120&fit=crop&crop=face",
    quote: "Dr. Jeff's keynote at our annual summit sparked conversations that are still happening months later. His ability to connect faith, leadership, and technology in a room full of diverse thinkers is rare and remarkable.",
    stars: 5,
    tag: "Speaking",
  },
  {
    name: "James O.",
    role: "SMB Owner, Horizon Retail",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&h=120&fit=crop&crop=face",
    quote: "I came in skeptical about AI for my small business. Dr. Jeff's consulting completely shifted my perspective. Within 60 days we had implemented three AI-driven workflows that saved us 15 hours a week.",
    stars: 5,
    tag: "Business Consulting",
  },
];

const TAG_COLORS = {
  "Business Consulting": "text-blue-300 bg-blue-500/10 border-blue-500/20",
  "Coaching & Training": "text-purple-300 bg-purple-500/10 border-purple-500/20",
  "Media & Podcasts": "text-orange-300 bg-orange-500/10 border-orange-500/20",
  "Speaking": "text-green-300 bg-green-500/10 border-green-500/20",
};

function StarRating({ count }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: count }).map((_, i) => (
        <SafeIcon key={i} icon={FiStar} className="h-4 w-4 fill-orange-400 text-orange-400" />
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      setActive(prev => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(id);
  }, [auto]);

  const go = (dir) => {
    setAuto(false);
    setActive(prev => (prev + dir + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  return (
    <section className="relative px-6 py-20 md:px-10 lg:px-16">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          className="mb-14 max-w-3xl"
        >
          <div className="mb-4 inline-flex items-center rounded-full border border-white/15 bg-white/5 px-4 py-1 text-xs uppercase tracking-[0.28em] text-blue-100/80">
            Testimonials
          </div>
          <h2 className="text-3xl font-semibold tracking-tight text-white md:text-5xl">
            Words from those who've experienced the difference
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-7 text-white/70 md:text-lg">
            Real feedback from leaders, creators, and organizations transformed by Dr. Jeff's work.
          </p>
        </motion.div>

        {/* Featured Testimonial */}
        <div className="relative mb-8 overflow-hidden rounded-[32px] border border-white/10 bg-white/5 p-[1px]">
          <div className="rounded-[31px] bg-[#0b1224]/80 backdrop-blur-xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="grid gap-8 p-8 md:grid-cols-[auto_1fr] md:p-12"
              >
                {/* Avatar */}
                <div className="flex flex-col items-center gap-4 md:items-start">
                  <div className="relative">
                    <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-blue-500 via-purple-500 to-orange-500 opacity-70 blur-sm" />
                    <img
                      src={TESTIMONIALS[active].avatar}
                      alt={TESTIMONIALS[active].name}
                      className="relative h-20 w-20 rounded-full object-cover ring-2 ring-white/10"
                    />
                  </div>
                  <div className="text-center md:text-left">
                    <div className="font-semibold text-white">{TESTIMONIALS[active].name}</div>
                    <div className="mt-0.5 text-xs text-white/50">{TESTIMONIALS[active].role}</div>
                  </div>
                  <StarRating count={TESTIMONIALS[active].stars} />
                  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] uppercase tracking-widest ${TAG_COLORS[TESTIMONIALS[active].tag]}`}>
                    {TESTIMONIALS[active].tag}
                  </span>
                </div>

                {/* Quote */}
                <div className="flex flex-col justify-center">
                  <div className="mb-6 text-5xl font-serif leading-none text-white/20 select-none">"</div>
                  <p className="text-xl leading-9 text-white/85 md:text-2xl md:leading-10">
                    {TESTIMONIALS[active].quote}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Decorative glow */}
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-purple-500/15 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-blue-500/10 blur-[80px]" />
        </div>

        {/* Controls + Dots */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => { setAuto(false); setActive(i); }}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-8 bg-blue-400' : 'w-2 bg-white/20 hover:bg-white/40'}`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => go(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/60 transition hover:bg-white/10 hover:text-white"
            >
              <SafeIcon icon={FiChevronLeft} className="h-5 w-5" />
            </button>
            <button
              onClick={() => go(1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/60 transition hover:bg-white/10 hover:text-white"
            >
              <SafeIcon icon={FiChevronRight} className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Thumbnail Row */}
        <div className="mt-8 grid grid-cols-5 gap-3">
          {TESTIMONIALS.map((t, i) => (
            <button
              key={i}
              onClick={() => { setAuto(false); setActive(i); }}
              className={`group relative overflow-hidden rounded-2xl border p-4 text-left transition-all duration-300 ${i === active ? 'border-blue-500/50 bg-blue-500/10' : 'border-white/5 bg-white/5 hover:border-white/15 hover:bg-white/10'}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <img src={t.avatar} alt={t.name} className="h-7 w-7 rounded-full object-cover" />
                <span className={`text-xs font-medium truncate ${i === active ? 'text-white' : 'text-white/50 group-hover:text-white/80'}`}>
                  {t.name.split(' ')[0]}
                </span>
              </div>
              <p className={`text-[10px] leading-relaxed line-clamp-2 ${i === active ? 'text-white/70' : 'text-white/30 group-hover:text-white/50'}`}>
                {t.quote.slice(0, 60)}…
              </p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}